import { navigate } from '../../router/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { bootOnce } from '../../bootstrap';
import { IconHeart, IconRefresh, IconSearch } from '../../ui/icons';
import { trackPageView, trackImpression, trackModelView, trackFavoriteAdd, trackFavoriteRemove, trackEvent } from '../../analytics';
import { normalizeAssetUrl } from '../../utils/urls';
import { toThumbUrl } from '../../utils/thumbUrl';
import { useI18n } from '../../bootstrap/i18n';
import { getTelegramId, requestAuthRequired } from '../../utils/telegramUser';
import { apiJsonOrThrow, fetchJson, getAccessTokenOrNull } from '../../utils/api';
import { reportHandledError } from '../../monitoring/uiErrorSink';
import { clearCachedModels, getCachedModels, setCachedModels, getCachedFavorites, setCachedFavorites } from './storefrontCache';
import { ModelEntrySheet } from './ModelEntrySheet';

type ModelRow = {
  id: number;
  name: string;
  topic?: string | null;
  image_url?: string | null;
  video_url?: string | null;
  has_video?: boolean | null;
  discount_text?: string | null;
  is_online?: boolean | null;
  is_active?: boolean | null;
  badges?: string[] | null;
  filters?: any;
  filter_age?: string | null;
  filter_appearance?: string | null;
  filter_origin?: string | null;
  filter_style?: string | null;
  // Ads model fields
  is_ad?: boolean | null;
  external_url?: string | null;
  outbound_url?: string | null;
  internal_route?: string | null;
  ad_format?: '1x1' | '1x2' | 'full' | null;
  // Social proof
  views?: number | null;
  private_show_enabled?: boolean | null;
  private_show_price_credits?: number | null;
  private_show_message_price_credits?: number | null;
  private_show_command_price_credits?: number | null;
  private_show_entry_modal_enabled?: boolean | null;
  direct_telegram_enabled?: boolean | null;
  direct_telegram_mode?: string | null;
  direct_telegram_paid_media_capable?: boolean | null;
  direct_telegram_price_stars_min?: number | null;
};

function formatCount(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(n);
}

function modelImageUrl(url?: string | null) {
  const vw = (document.documentElement?.clientWidth || window.innerWidth || 390);
  const pagePadTight = 8;
  const gridGap = 12;
  const cardCssW = Math.max(140, Math.min(320, Math.floor((vw - pagePadTight * 2 - gridGap) / 2)));
  const cardCssH = Math.round(cardCssW * (4 / 3));
  return toThumbUrl(url, { cssWidth: cardCssW, cssHeight: cardCssH });
}

type ActiveFilters = { age: string; appearance: string; origin: string; style: string };
type StorefrontInternalRoute = 'storefront' | 'profile' | 'tasks' | 'gifts';

function normalizeInternalRoute(raw: string | null | undefined): StorefrontInternalRoute | null {
  const value = String(raw || '').trim();
  if (value === 'storefront' || value === 'profile' || value === 'tasks' || value === 'gifts') return value;
  return null;
}

function getModelFilters(m: ModelRow): ActiveFilters {
  const f = (m.filters && typeof m.filters === 'object') ? m.filters : {};
  return {
    age: String(f.age || m.filter_age || ''),
    appearance: String(f.appearance || m.filter_appearance || ''),
    origin: String(f.origin || m.filter_origin || ''),
    style: String(f.style || m.filter_style || ''),
  };
}

export function StorefrontRoute(props: {
  isPremium?: boolean | null;
  premiumBenefits?: { hide_ad_models: boolean; hide_surprise_offers: boolean } | null;
  onOnlineUsersCount?: (count: number) => void;
  onOpenTopup?: () => void;
} = {}) {
  const services = useMemo(() => bootOnce(), []);
  const { t } = useI18n(services.i18n);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [models, setModels] = useState<ModelRow[]>([]);
  const [onlineUsersCount, _setOnlineUsersCount] = useState(0);
  const setOnlineUsersCount = useCallback((n: number) => {
    _setOnlineUsersCount(n);
    props.onOnlineUsersCount?.(n);
  }, [props.onOnlineUsersCount]);
  // No default filter: show all models until user selects a badge.
  const [activeBadge, setActiveBadge] = useState<'popular' | 'hot' | 'premium' | 'new' | 'vip' | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [favorites, setFavorites] = useState<Set<number>>(new Set());
  const [filters, setFilters] = useState<ActiveFilters>({ age: '', appearance: '', origin: '', style: '' });
  const [entryModel, setEntryModel] = useState<ModelRow | null>(null);

  // ==========================================================
  // Storefront impressions (viewability-based)
  // - 100% visibility threshold
  // - dwell time 1000ms
  // - dedupe once per WebApp session (in-memory)
  // ==========================================================
  const IMPRESSION_THRESHOLD = 1; // 100%
  const IMPRESSION_DWELL_MS = 1000;

  const impressionSeenRef = useRef<Set<number>>(new Set());
  const impressionTimersRef = useRef<Map<number, number>>(new Map());
  const impressionElRef = useRef<Map<number, HTMLElement>>(new Map());
  const impressionMetaRef = useRef<Map<number, { name: string; privateShowEnabled?: boolean; priceCredits?: number | null }>>(new Map());
  const impressionObserverRef = useRef<IntersectionObserver | null>(null);

  const isPageVisible = () => {
    try {
      return document.visibilityState === 'visible';
    } catch {
      return true;
    }
  };

  const isFullyInViewport = (el: HTMLElement) => {
    try {
      const r = el.getBoundingClientRect();
      const vw = window.innerWidth || document.documentElement.clientWidth || 0;
      const vh = window.innerHeight || document.documentElement.clientHeight || 0;
      if (!vw || !vh) return false;
      return r.top >= 0 && r.left >= 0 && r.bottom <= vh && r.right <= vw;
    } catch {
      return false;
    }
  };

  const clearImpressionTimer = useCallback((modelId: number) => {
    const t = impressionTimersRef.current.get(modelId);
    if (t) window.clearTimeout(t);
    impressionTimersRef.current.delete(modelId);
  }, []);

  const fireImpressionIfStillEligible = useCallback((modelId: number) => {
    if (!isPageVisible()) return;
    if (impressionSeenRef.current.has(modelId)) return;
    const el = impressionElRef.current.get(modelId);
    if (!el) return;
    if (!isFullyInViewport(el)) return;
    const meta = impressionMetaRef.current.get(modelId);
    trackImpression(modelId, meta?.name);
    if (meta?.privateShowEnabled) {
      trackEvent('private_show_cta_view', {
        modelId,
        modelName: meta.name,
        origin: 'storefront',
        metadata: { price_credits: meta.priceCredits ?? null },
      });
    }
    impressionSeenRef.current.add(modelId);
    clearImpressionTimer(modelId);
  }, [clearImpressionTimer]);

  const scheduleImpression = useCallback((modelId: number) => {
    if (!isPageVisible()) return;
    if (impressionSeenRef.current.has(modelId)) return;
    if (impressionTimersRef.current.has(modelId)) return;
    const t = window.setTimeout(() => {
      impressionTimersRef.current.delete(modelId);
      fireImpressionIfStillEligible(modelId);
    }, IMPRESSION_DWELL_MS);
    impressionTimersRef.current.set(modelId, t);
  }, [fireImpressionIfStillEligible]);

  useEffect(() => {
    // If the page becomes hidden, cancel pending dwell timers to avoid false positives.
    const onVis = () => {
      if (isPageVisible()) {
        // When returning to foreground, re-check currently visible cards and schedule dwell timers.
        for (const [id, el] of impressionElRef.current.entries()) {
          if (impressionSeenRef.current.has(id)) continue;
          if (isFullyInViewport(el)) scheduleImpression(id);
          else clearImpressionTimer(id);
        }
        return;
      }
      for (const id of impressionTimersRef.current.keys()) clearImpressionTimer(id);
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [clearImpressionTimer, scheduleImpression]);

  useEffect(() => {
    // Create observer once; cards register themselves via ref callbacks below.
    // NOTE: we intentionally use viewport as root (null). If we later move storefront into
    // a dedicated scroll container, we can pass `root` here.
    let obs: IntersectionObserver | null = null;
    try {
      obs = new IntersectionObserver((entries) => {
        for (const e of entries) {
          const el = e.target as HTMLElement;
          const raw = String(el?.dataset?.modelId || '').trim();
          const modelId = Number(raw || 0);
          if (!Number.isFinite(modelId) || modelId <= 0) continue;
          if (impressionSeenRef.current.has(modelId)) {
            clearImpressionTimer(modelId);
            continue;
          }
          const fullyVisible = Boolean(e.isIntersecting) && (e.intersectionRatio >= IMPRESSION_THRESHOLD);
          if (!fullyVisible) {
            clearImpressionTimer(modelId);
            continue;
          }
          scheduleImpression(modelId);
        }
      }, { threshold: [IMPRESSION_THRESHOLD] });
    } catch {
      obs = null;
    }

    impressionObserverRef.current = obs;
    if (obs) {
      // Observe cards already mounted (if any).
      for (const el of impressionElRef.current.values()) {
        try { obs.observe(el); } catch {}
      }
    }

    return () => {
      try { obs?.disconnect(); } catch {}
      impressionObserverRef.current = null;
      for (const id of impressionTimersRef.current.keys()) clearImpressionTimer(id);
    };
  }, [clearImpressionTimer, scheduleImpression]);

  const registerImpressionEl = useCallback((modelId: number, modelName: string, el: HTMLElement | null, meta?: { privateShowEnabled?: boolean; priceCredits?: number | null }) => {
    if (!modelId || modelId <= 0) return;
    if (!el) {
      const prev = impressionElRef.current.get(modelId);
      if (prev && impressionObserverRef.current) {
        try { impressionObserverRef.current.unobserve(prev); } catch {}
      }
      impressionElRef.current.delete(modelId);
      impressionMetaRef.current.delete(modelId);
      clearImpressionTimer(modelId);
      return;
    }

    el.dataset.modelId = String(modelId);
    impressionElRef.current.set(modelId, el);
    impressionMetaRef.current.set(modelId, {
      name: String(modelName || ''),
      privateShowEnabled: meta?.privateShowEnabled === true,
      priceCredits: meta?.priceCredits ?? null,
    });

    // Observe + handle "already visible above the fold" case immediately.
    if (impressionObserverRef.current) {
      try { impressionObserverRef.current.observe(el); } catch {}
    }
    if (!impressionSeenRef.current.has(modelId) && isPageVisible() && isFullyInViewport(el)) {
      scheduleImpression(modelId);
    }
  }, [clearImpressionTimer, scheduleImpression]);

  useEffect(() => {
    let cancelled = false;

    // Stale-while-revalidate: render cached data instantly, refresh in background.
    const cached = getCachedModels();
    const cachedFavs = getCachedFavorites();
    if (cached) {
      setModels(cached.rows);
      setOnlineUsersCount(cached.onlineUsersCount);
      if (cachedFavs) setFavorites(cachedFavs);
      setLoading(false);
    }

    (async () => {
      if (!cached) { setLoading(true); setErr(null); }
      try {
        // /api/models is public — don't wait for JWT on first load (saves ~500ms).
        // On subsequent loads the cache renders instantly and we can afford to wait.
        const tokenP = getAccessTokenOrNull(services, { timeoutMs: cached ? 500 : 0 });
        const token = cached ? await tokenP : (await Promise.race([tokenP, Promise.resolve(null)]));
        const r = await apiJsonOrThrow<{ ok: true; data: ModelRow[]; order_mode?: 'manual' | 'random'; online_users_count?: number }>(
          '/api/models',
          token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
          { feature: 'storefront', endpoint: '/api/models' }
        );
        if (cancelled) return;
        const rows = ((r as any)?.data || []) as ModelRow[];
        const onlineCount = Number((r as any)?.online_users_count) || 0;
        setModels(rows);
        setOnlineUsersCount(onlineCount);
        if ((r as any)?.order_mode === 'random') clearCachedModels();
        else setCachedModels(rows, onlineCount);
      } catch (e) {
        if (cancelled) return;
        if (!cached) {
          setErr(reportHandledError({
            err: e,
            code: 'storefront.models_failed',
            userMessage: t('error.loading', 'Error loading data'),
            severity: 'error',
            context: { feature: 'storefront', endpoint: '/api/models', stage: 'fetch_exception' },
          }));
          setLoading(false);
          return;
        }
      }

      trackPageView('storefront');

      try {
        const telegramId = getTelegramId();
        if (telegramId) {
          const accessToken = await getAccessTokenOrNull(services, { timeoutMs: 2000 });
          if (accessToken) {
            const favRes = await fetchJson<{ ok: boolean; model_ids: number[] }>('/api/user/favorites', {
              headers: { Authorization: `Bearer ${accessToken}` },
            });
            if (favRes.ok) {
              const ids = ((favRes.data as any)?.model_ids || []) as number[];
              const favSet = new Set<number>(ids.map((x) => Number(x)));
              setFavorites(favSet);
              setCachedFavorites(favSet);
            }
          }
        }
      } catch {}

      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const visibleModels = useMemo(() => {
    let list = models.slice();

    // Premium benefit: hide ad models
    if (props.isPremium === true && props.premiumBenefits?.hide_ad_models) {
      list = list.filter((m) => !m.is_ad);
    }

    if (favoritesOnly) {
      list = list.filter((m) => favorites.has(m.id));
    }

    // Badge filter (strict): when a badge is selected, show only models that contain it.
    if (activeBadge) {
      list = list.filter((m) => {
        const bs = new Set((Array.isArray(m.badges) ? m.badges : []).map((x) => String(x || '').trim().toLowerCase()).filter(Boolean));
        return bs.has(activeBadge);
      });
    }

    const hasAny = !!(filters.age || filters.appearance || filters.origin || filters.style);
    if (hasAny) {
      list = list.filter((m) => {
        const mf = getModelFilters(m);
        const matchAge = !filters.age || mf.age === filters.age;
        const matchAppearance = !filters.appearance || mf.appearance === filters.appearance;
        const matchOrigin = !filters.origin || mf.origin === filters.origin;
        const matchStyle = !filters.style || mf.style === filters.style;
        return matchAge && matchAppearance && matchOrigin && matchStyle;
      });
    }

    return list;
  }, [models, favoritesOnly, favorites, activeBadge, filters, props.isPremium, props.premiumBenefits]);

  async function toggleFavorite(modelId: number) {
    const telegramId = getTelegramId();
    if (!telegramId) {
      requestAuthRequired({ path: window.location.pathname || '/' });
      return;
    }

    const accessToken = await getAccessTokenOrNull(services, { timeoutMs: 2500 });
    if (!accessToken) {
      // Don't show scary errors on storefront. If auth isn't ready, ask user to open in Telegram.
      requestAuthRequired({ path: window.location.pathname || '/' });
      return;
    }

    const isFav = favorites.has(modelId);

    try {
      await fetchJson('/api/user/favorites/toggle', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ modelId, value: !isFav }),
      });

      setFavorites((prev) => {
        const next = new Set(prev);
        if (isFav) next.delete(modelId);
        else next.add(modelId);
        return next;
      });

      // Analytics: track favorite change
      if (isFav) {
        trackFavoriteRemove(modelId);
      } else {
        trackFavoriteAdd(modelId);
      }

      const tg = (window as any).Telegram?.WebApp;
      tg?.HapticFeedback?.notificationOccurred?.(isFav ? 'warning' : 'success');
    } catch (e: any) {
      // Best-effort: don't block UX with errors here.
      const tg = (window as any).Telegram?.WebApp;
      tg?.HapticFeedback?.notificationOccurred?.('warning');
    }
  }

  async function openDirectTelegram(model: ModelRow) {
    const modelId = Number(model.id || 0);
    if (!modelId) return;
    try {
      trackEvent('direct_telegram_cta_click', {
        modelId,
        modelName: model.name,
        origin: 'storefront',
        metadata: {
          mode: model.direct_telegram_mode || null,
          paid_media_capable: model.direct_telegram_paid_media_capable === true,
          price_stars_min: model.direct_telegram_price_stars_min ?? null,
        },
      });
    } catch {}
    try {
      const token = await getAccessTokenOrNull(services, { timeoutMs: 1200 });
      const r = await fetchJson<any>(`/api/models/${modelId}/private-bot-click`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: JSON.stringify({ source: 'storefront_direct_telegram' }),
      });
      const data = (r as any)?.data || {};
      const url = String(data?.data?.outbound_url || data?.outbound_url || data?.data?.open_url || data?.open_url || data?.data?.direct_url || data?.direct_url || '').trim();
      if (!url) return;
      const tg = (window as any).Telegram?.WebApp;
      const isTelegramUrl = /^https?:\/\/(t\.me|telegram\.me)\//i.test(url);
      if (isTelegramUrl && tg?.openTelegramLink) tg.openTelegramLink(url);
      else if (tg?.openLink) tg.openLink(url);
      else window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e: any) {
      reportHandledError({
        err: e,
        code: 'storefront.direct_telegram_failed',
        userMessage: '',
        severity: 'warning',
        context: { feature: 'storefront', endpoint: '/api/models/:id/private-bot-click', modelId },
      });
    }
  }

  // Synchronous thumb URL resolver. We deliberately do NOT post-measure the
  // rendered card and re-fetch at a slightly different size — that produced
  // a duplicate cold-cache miss for every visible card on first paint, since
  // the bucket size already snaps requests to a coarse grid (see thumbUrl.ts).
  const modelThumbUrl = (url?: string | null): string | null => {
    const normalized = normalizeAssetUrl(url);
    if (!normalized) return null;
    return modelImageUrl(normalized);
  };

  return (
    <div ref={rootRef}>
      <div class="tabsRow">
        {(['popular', 'hot', 'premium', 'new', 'vip'] as const).map((tab) => (
          <button
            key={tab}
            class={`tabBtn ${activeBadge === tab ? 'active' : ''}`}
            onClick={() => setActiveBadge((prev) => (prev === tab ? null : tab))}
          >
            {tab === 'popular'
              ? t('category.popular', 'Popular')
              : tab === 'hot'
                ? t('category.hot', 'Hot')
                : tab === 'premium'
                  ? t('category.premium', 'Premium')
                  : tab === 'new'
                    ? t('category.new', 'New')
                    : t('category.vip', 'VIP')}
          </button>
        ))}

        <button
          class={`tabBtn icon ${favoritesOnly ? 'active' : ''}`}
          title={t('category.favorites', 'Favorites')}
          onClick={() => setFavoritesOnly((v) => !v)}
        >
          <IconHeart filled={favoritesOnly} />
        </button>
        <button
          class={`tabBtn icon ${filtersOpen ? 'active' : ''}`}
          title={t('btn.filters', 'Filters')}
          onClick={() => setFiltersOpen((v) => !v)}
        >
          <IconSearch />
        </button>
      </div>

      {filtersOpen && (
        <div class="card" style={{ padding: 12, marginBottom: 10 }}>
          <div class="filtersGrid">
            <label class="filterField">
              <span class="muted">{t('filter.age', 'Age')}</span>
              <select class="selectInput" value={filters.age} onChange={(e) => setFilters((p) => ({ ...p, age: (e.currentTarget as HTMLSelectElement).value }))}>
                <option value="">{t('filter.all', 'All')}</option>
                <option value="18-22">18-22</option>
                <option value="22-30">22-30</option>
                <option value="30-40">30-40</option>
              </select>
            </label>
            <label class="filterField">
              <span class="muted">{t('filter.appearance', 'Appearance')}</span>
              <select class="selectInput" value={filters.appearance} onChange={(e) => setFilters((p) => ({ ...p, appearance: (e.currentTarget as HTMLSelectElement).value }))}>
                <option value="">{t('filter.all', 'All')}</option>
                <option value="Blonde">{t('filter.blonde', 'Blonde')}</option>
                <option value="Brunette">{t('filter.brunette', 'Brunette')}</option>
                <option value="Redhead">{t('filter.redhead', 'Redhead')}</option>
              </select>
            </label>
            <label class="filterField">
              <span class="muted">{t('filter.origin', 'Origin')}</span>
              <select class="selectInput" value={filters.origin} onChange={(e) => setFilters((p) => ({ ...p, origin: (e.currentTarget as HTMLSelectElement).value }))}>
                <option value="">{t('filter.all', 'All')}</option>
                <option value="European">{t('filter.european', 'European')}</option>
                <option value="Asian">{t('filter.asian', 'Asian')}</option>
                <option value="Latina">{t('filter.latina', 'Latina')}</option>
              </select>
            </label>
            <label class="filterField">
              <span class="muted">{t('filter.style', 'Style')}</span>
              <select class="selectInput" value={filters.style} onChange={(e) => setFilters((p) => ({ ...p, style: (e.currentTarget as HTMLSelectElement).value }))}>
                <option value="">{t('filter.all', 'All')}</option>
                <option value="Natural">{t('filter.natural', 'Natural')}</option>
                <option value="Glamorous">{t('filter.glamorous', 'Glamorous')}</option>
                <option value="Sporty">{t('filter.sporty', 'Sporty')}</option>
                <option value="Cute">{t('filter.cute', 'Cute')}</option>
              </select>
            </label>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button class="secondaryBtn" onClick={() => setFilters({ age: '', appearance: '', origin: '', style: '' })} style={{ flex: 1, justifyContent: 'center' }}>
              {t('btn.reset', 'Reset')}
            </button>
            <button class="primaryBtn" onClick={() => setFiltersOpen(false)} style={{ flex: 1, justifyContent: 'center' }}>
              {t('btn.apply', 'Apply')}
            </button>
          </div>
        </div>
      )}

      {loading && <div class="muted" style={{ padding: 12 }}>{t('store.loading_models', 'Loading models...')}</div>}
      {err && (
        <div class="card" style={{ padding: 12, borderColor: 'rgba(255,90,95,0.35)' }}>
          <div style={{ color: 'rgba(255,90,95,0.95)', fontWeight: 800 }}>{err}</div>
          <div style={{ marginTop: 10 }}>
            <button class="iconBtn" onClick={() => window.location.reload()}>
              <IconRefresh />
            </button>
          </div>
        </div>
      )}

      {!loading && !err && (
        <div class="modelGrid" id="modelsGrid">
          {visibleModels.map((m) => {
            const isFav = favorites.has(m.id);
            const isLive = !!m.is_online;
            const isModelOfDay = Boolean((m as any).is_model_of_day);
            const badgeSet = new Set((Array.isArray(m.badges) ? m.badges : []).map((x) => String(x || '').trim().toLowerCase()).filter(Boolean));
            const thumbSrc = modelThumbUrl(m.image_url);
            const showPrivateShow = !m.is_ad && m.private_show_enabled === true;
            const showDirectTelegram = !m.is_ad && m.direct_telegram_enabled === true;
            // Determine grid span for ad banners
            const adFormat = m.is_ad ? (m.ad_format || '1x1') : '1x1';
            const adSpanClass = adFormat === '1x2' ? 'adSpan2' : adFormat === 'full' ? 'adSpanFull' : '';
            return (
              <div
                key={m.id}
                class={`card modelCard ${isModelOfDay ? 'modelOfDay' : ''} ${adSpanClass}`}
                ref={(el) => registerImpressionEl(m.id, m.name, el as any, {
                  privateShowEnabled: showPrivateShow,
                  priceCredits: m.private_show_price_credits ?? null,
                })}
                onContextMenu={(e) => {
                  // Extra guard: disable context menu on cards (mobile long-press).
                  e.preventDefault();
                }}
                onClick={() => {
                trackModelView(m.id, m.name);
                // Handle ads model - internal route or external URL
                if (m.is_ad) {
                  // IMPORTANT: Telegram.WebApp.openLink() only works when called
                  // synchronously in response to user click. Any async operation
                  // (like await) before openLink will cause Telegram to ignore it.
                  
                  // 1. Navigate FIRST (synchronously!)
                  const internalRoute = normalizeInternalRoute(m.internal_route);
                  if (internalRoute === 'tasks') {
                    navigate({ name: 'tasks', from: 'storefront' });
                  } else if (internalRoute) {
                    navigate({ name: internalRoute });
                  } else if (m.external_url || (m as any).outbound_url) {
                    const url = String(((m as any).outbound_url || m.external_url) || '').trim();
                    if (!url) return;
                    const tg = (window as any).Telegram?.WebApp;
                    if (tg?.openLink) {
                      tg.openLink(url);
                    } else {
                      window.open(url, '_blank', 'noopener,noreferrer');
                    }
                  }
                  
                  return;
                }
                const entryModalEnabled = !m.is_ad && m.private_show_entry_modal_enabled !== false;
                if (entryModalEnabled) setEntryModel(m);
                else navigate({ name: 'chat', modelId: String(m.id) });
              }}
              >
                <div class="modelThumbWrap">
                  {thumbSrc ? (
                    <img
                      class="modelThumb"
                      src={thumbSrc}
                      alt={m.name}
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        const img = e.currentTarget as HTMLImageElement;
                        // Keep layout (aspect-ratio) so overlay stays visible.
                        img.style.opacity = '0';
                        try { img.closest('.modelThumbWrap')?.classList.add('thumbMissing'); } catch {}
                      }}
                    />
                  ) : (
                    <div class="modelThumb modelThumbPlaceholder" aria-hidden="true" />
                  )}
                  {isModelOfDay && <div class="badgeTop badgeModelOfDay">★ {t('store.model_of_day', 'MODEL OF THE DAY')}</div>}
                  {!isModelOfDay && m.discount_text && <div class="badgeTop badgeDiscount">{m.discount_text}</div>}
                  {!!m.has_video && !m.is_ad && <div class="badgeTop badgeVideo">▶ {t('store.video', 'VIDEO')}</div>}
                  {m.is_ad && <div class="badgeTop badgeAd">{t('store.ad', 'AD')}</div>}
                  {badgeSet.has('voting') && <div class="badgeTop badgeVoting">{t('category.voting', 'Voting')}</div>}
                  {!m.is_ad && (m.views ?? 0) > 0 && (
                    <div class="badgeViews">
                      <span class="badgeViewsIcon">👁</span>
                      {formatCount(m.views!)}
                    </div>
                  )}
                  <button
                    class={`favBtn ${isFav ? 'on' : ''}`}
                    title={t('store.favorite', 'Favorite')}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(m.id);
                    }}
                  >
                    <IconHeart filled={isFav} />
                  </button>

                  {/* Overlay: name + properties on top of the photo (like legacy storefront) */}
                  <div class="modelOverlay">
                    <div class="modelOverlayRow">
                      <div class="modelOverlayName" title={m.name}>
                        {isLive && <span class="nameLiveDot" aria-hidden="true"></span>}
                        {m.name}
                      </div>
                    </div>
                    <div class="modelOverlayRow chips">
                      {badgeSet.has('vip') && <span class="mediaChip">{t('category.vip', 'VIP')}</span>}
                      {badgeSet.has('premium') && <span class="mediaChip">{t('category.premium', 'Premium')}</span>}
                      {badgeSet.has('hot') && <span class="mediaChip">{t('category.hot', 'Hot')}</span>}
                      {badgeSet.has('new') && <span class="mediaChip">{t('category.new', 'New')}</span>}
                      {badgeSet.has('popular') && <span class="mediaChip">{t('category.popular', 'Popular')}</span>}
                      {!isLive && <span class="mediaChip offChip">{t('store.off', 'OFF')}</span>}
                      {favoritesOnly && <span class="mediaChip">{t('category.favorites', 'Favorites')}</span>}
                    </div>
                    {(showPrivateShow || showDirectTelegram) && (
                      <div class="modelOverlayRow" style={{ gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                        {showPrivateShow && (
                          <button
                            type="button"
                            class="mediaChip"
                            style={{ border: '1px solid rgba(255,255,255,0.22)', cursor: 'pointer' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              trackEvent('private_show_cta_click', {
                                modelId: m.id,
                                modelName: m.name,
                                origin: 'storefront',
                                metadata: { price_credits: m.private_show_price_credits ?? null },
                              });
                              setEntryModel(m);
                            }}
                          >
                            {t('store.private_show', 'Private show')}
                          </button>
                        )}
                        {showDirectTelegram && (
                          <button
                            type="button"
                            class="mediaChip"
                            style={{ border: '1px solid rgba(255,255,255,0.22)', cursor: 'pointer' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              void openDirectTelegram(m);
                            }}
                          >
                            {t('store.telegram_chat', 'Telegram chat')}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {entryModel && (
        <ModelEntrySheet
          model={{ id: entryModel.id, name: entryModel.name, topic: entryModel.topic, image_url: entryModel.image_url }}
          onClose={() => setEntryModel(null)}
          onOpenTopup={props.onOpenTopup}
        />
      )}
    </div>
  );
}
